import {gql} from '@apollo/client';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BLUE_LIGHT, GRAY_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';

import {
  SimplePolymerGraphQL,
  OligoGraphQL,
  ComplexPolymerType,
  COMPLEX_POLYMER_DISPLAY_NAMES,
  ConnectionGraphQL,
  ResolvedAttachmentGraphQL,
} from '../ComplexPolymerConstants';
import {PolymerStructureGraph} from '../PolymerStructureGraphConstants';
import {
  ComplexPolymerSwapOligoQuery,
  ComplexPolymerSwapOligoQueryVariables,
} from './__generated/ComplexPolymerSwapOligoQuery';
import ComplexPolymerComponentSearchModal, {
  ComplexPolymerComponentSearchModalProps,
} from './ComplexPolymerComponentSearchModal';
import {getCurrentOligoId, swapOligo} from './ComplexPolymerSwapOligoMenuUtil';

export type OligoConjugateSwapOligoGraphQL = ExtractByTypename<
  ComplexPolymerSwapOligoQuery['complexPolymer'],
  'OligoConjugateNode'
>;

export type OligoDuplexSwapOligoGraphQL = ExtractByTypename<
  ComplexPolymerSwapOligoQuery['complexPolymer'],
  'OligoDuplexNode'
>;

type OligoSchemaFieldGraphQL =
  | OligoConjugateSwapOligoGraphQL['oligoSchemaField']
  | OligoDuplexSwapOligoGraphQL['forwardStrandSchemaField']
  | OligoDuplexSwapOligoGraphQL['reverseStrandSchemaField'];

export const COMPLEX_POLYMER_SWAP_OLIGO_QUERY = gql`
  query ComplexPolymerSwapOligoQuery($complexPolymerId: StrictString!)
  @engComponent(name: MODIFIED_BIOLOGICS)
  @skipAudit {
    complexPolymer(id: $complexPolymerId) {
      ... on ComplexPolymerNode {
        id
      }
      ... on OligoConjugateNode {
        id
        oligoSchemaField {
          ... on ComplexPolymerSchemaFieldDefinitionNode {
            id
            schemaId
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ... on OligoDuplexNode {
        id
        forwardStrandSchemaField {
          ... on ComplexPolymerSchemaFieldDefinitionNode {
            id
            schemaId
          }
          ...InaccessibleInfoGraphQL
        }
        reverseStrandSchemaField {
          ... on ComplexPolymerSchemaFieldDefinitionNode {
            id
            schemaId
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ComplexPolymerSwapOligoMenuProps {
  complexPolymerId: string;
  complexPolymerVersionId: string;
  complexPolymerType: ComplexPolymerType;
  simplePolymers: Array<SimplePolymerGraphQL>;
  connections: Array<ConnectionGraphQL>;
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>;
  oligo: OligoGraphQL;
  structureGraph: PolymerStructureGraph | null;
  offset?: number | null;
  isForward?: boolean;
}

export default function ComplexPolymerSwapOligoMenu({
  complexPolymerId,
  complexPolymerVersionId,
  complexPolymerType,
  simplePolymers,
  connections,
  resolvedAttachments,
  oligo,
  structureGraph,
  offset,
  isForward,
}: ComplexPolymerSwapOligoMenuProps): JSX.Element {
  assert(
    complexPolymerType === ComplexPolymerType.OLIGO_CONJUGATE || offset != null,
    'Expected oligo duplex to have non-null offset'
  );

  function getOligoSchemaId(data: ComplexPolymerSwapOligoQuery): string {
    let oligoSchemaField: OligoSchemaFieldGraphQL;
    if (data.complexPolymer.__typename === 'OligoConjugateNode') {
      oligoSchemaField = data.complexPolymer.oligoSchemaField;
    } else {
      assert(
        data.complexPolymer.__typename === 'OligoDuplexNode',
        'Expected oligo conjugate or oligo duplex'
      );
      assert(isForward != null, 'Expected forward or reverse strand to be selected if oligo duplex');
      oligoSchemaField = isForward
        ? data.complexPolymer.forwardStrandSchemaField
        : data.complexPolymer.reverseStrandSchemaField;
    }

    assert(
      oligoSchemaField != null && !isInaccessible(oligoSchemaField) && oligoSchemaField.schemaId != null,
      'Expected schema field to link to entity schema '
    );

    return oligoSchemaField.schemaId;
  }

  return (
    <BenchlingQuery<ComplexPolymerSwapOligoQuery, ComplexPolymerSwapOligoQueryVariables>
      query={COMPLEX_POLYMER_SWAP_OLIGO_QUERY}
      variables={{
        complexPolymerId,
      }}
      renderLoading={() => <BenchlingIcon className={css(styles.disabledEditIcon)} icon="edit" />}
    >
      {(data) => {
        const oligoSchemaId = getOligoSchemaId(data);

        return (
          <Dropdown
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FIT_CONTENT}>
                <Option
                  icon="swap"
                  onSelect={() => {
                    runModal<ComplexPolymerComponentSearchModalProps>(ComplexPolymerComponentSearchModal, {
                      schemaIds: [oligoSchemaId],
                      componentTypes:
                        oligo.nucleotideType === NucleotideType.RNA
                          ? [ModelTypeConstants.RNA_OLIGO]
                          : [ModelTypeConstants.DNA_OLIGO],
                      onSubmit: async (selectedIds: Array<string>) => {
                        await swapOligo(
                          complexPolymerId,
                          complexPolymerVersionId,
                          complexPolymerType,
                          simplePolymers,
                          connections,
                          resolvedAttachments,
                          oligo,
                          selectedIds,
                          structureGraph,
                          offset
                        );
                      },
                      complexPolymerTypeDescriptor: COMPLEX_POLYMER_DISPLAY_NAMES[complexPolymerType],
                      titleText: 'Swap oligo',
                      submitButtonText: 'Swap',
                      rowSelectionMode: RowSelectionMode.SINGLE,
                      isDisabled: (selectedIds: Array<string>) =>
                        selectedIds.includes(getCurrentOligoId(simplePolymers, oligo)) ||
                        selectedIds.length !== 1,
                    });
                  }}
                >
                  Change oligo
                </Option>
              </Menu>
            }
          >
            <BenchlingIcon className={css(styles.editIcon)} icon="edit" />
          </Dropdown>
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  editIcon: {
    cursor: 'pointer',
    color: BLUE_LIGHT,
    marginLeft: '5px',
  },
  disabledEditIcon: {
    color: GRAY_LIGHT,
    marginLeft: '5px',
  },
});
